import React, { useContext, useState } from "react";
import {
  View,
  FlatList,
  Text,
  TouchableOpacity,
  StyleSheet,
  SectionList,
} from "react-native";

// import { Ionicons } from '@expo/vector-icons';
// import Icon from 'react-native-ionicons';
import { Ionicons as Icon, Foundation as FIcon } from "@expo/vector-icons";
import { ProductContext } from "../../contexts/product/Store";

import AddEditCartItemModal from "../modals/AddEditCartItemModal";
import { AnalyticsService } from "../../constants/Analytics";
import { AuthContext } from "../../contexts/auth/Store";
import ProductView from "./ProductView";
import ProductViewList from "./ProductViewList";
import FontStyles from "../../constants/FontStyle";
import Colors from "../../constants/Colors";
import { IProduct } from "../../interfaces/product";

const analytics = new AnalyticsService();

type TSectionKeyType = "exact" | "match" | "history" | "all";
type TProductSectionKey = TSectionKeyType;
type TProductSectionType = TSectionKeyType;

interface ISectionData {
  key: TProductSectionKey;
  list: IProduct[];
}

interface ISection {
  key: TProductSectionKey;
  type: TProductSectionType;
  data: ISectionData[];
}

interface IProps {
  products: (IProduct | any)[];
  title?: string;
  showHeading?: boolean;
  isLoading?: boolean;
  footerComponent?: React.FC;
  onEndReached?: Function;
}

const ProductStack: React.FC<IProps> = function ({
  products,
  title,
  showHeading,
  isLoading,
  footerComponent,
  onEndReached,
}) {
  const auth = useContext(AuthContext);
  const buyer = auth.getUserData.buyerData;

  const { product_state, product_dispatch } = useContext(ProductContext);

  const [modalState, setModalState]: [any, any] = useState(false);
  const [product, setProduct]: [any, any] = useState(null);

  const hideModal = () => {
    setModalState(false);
    setProduct(null);
  };

  const openProductModal = (item: IProduct) => {
    setProduct(item);
    setModalState(true);

    analytics.logEvent("Viewed Product", buyer, {
      product_id: item._id,
      product_name: item.name,
    });
    analytics.googleHelpers.setCurrentScreen("ViewedProduct", "ViewedProduct");
  };

  const setProductView = (config: string) => {
    product_dispatch({ type: "SET_PRODUCT_VIEW", payload: config });
  };

  const SingleProduct = function ({
    item,
    index,
  }: {
    item: IProduct;
    index: any;
  }) {
    return (
      <>
        <View
          key={item._id}
          style={[
            product_state?.product_view === "grid"
              ? styles.itemWrapper
              : styles.itemWrapperList,
            product_state?.product_view === "grid" && index % 2
              ? styles.itemWrapperBorder
              : null,
          ]}
        >
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => {
              openProductModal(item);
            }}
            style={[product_state?.product_view === "grid" && { flex: 1 }]}
          >
            {product_state?.product_view === "grid" ? (
              <ProductView product={item} />
            ) : (
              <ProductViewList product={item} />
            )}
          </TouchableOpacity>
        </View>
      </>
    );
  };

  const DummyProduct: React.FC<any> = function () {
    return (
      <>
        {product_state.product_view === "grid" ? (
          <View style={styles.itemWrapper}>
            <View style={styles.dummyCard}></View>
          </View>
        ) : (
          <View style={styles.itemWrapperList}>
            <View style={[styles.dummyCard, { minHeight: 100 }]}></View>
          </View>
        )}
      </>
    );
  };

  return (
    <>
      {showHeading && (
        <View style={styles.listMenu}>
          {isLoading ? (
            <Text style={styles.listMenuText}>Fetching products</Text>
          ) : title ? (
            <Text style={styles.listMenuText}>
              {title} ({products?.length || "0"})
            </Text>
          ) : (
            products && (
              <Text style={styles.listMenuText}>
                {products?.length || "0"} product{products?.length > 1 && "s"}
              </Text>
            )
          )}
          <TouchableOpacity
            style={styles.listMenuIcon}
            onPress={() => setProductView("grid")}
          >
            <Icon
              name="grid"
              size={18}
              color={
                product_state.product_view === "grid"
                  ? Colors.tintColor
                  : Colors.shades._1
              }
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.listMenuIcon}
            onPress={() => setProductView("list")}
          >
            <Icon
              name="menu"
              size={24}
              color={
                product_state.product_view === "list"
                  ? Colors.tintColor
                  : Colors.shades._1
              }
            />
          </TouchableOpacity>
        </View>
      )}

      {isLoading ? (
        <FlatList
          initialNumToRender={5}
          data={[0, 1, 2, 3]}
          renderItem={DummyProduct}
          keyExtractor={(item) => "_" + item}
          key={product_state.product_view === "list" ? 1 : 2}
          numColumns={product_state.product_view === "list" ? 1 : 2}
          contentContainerStyle={styles.listWrapper}
        />
      ) : products.length && product_state.product_view ? (
        <FlatList
          initialNumToRender={products.length}
          data={products || []}
          renderItem={SingleProduct}
          keyExtractor={(x: any) => x._id}
          key={product_state.product_view === "list" ? 1 : 2}
          numColumns={product_state.product_view === "list" ? 1 : 2}
          ListFooterComponent={footerComponent || null}
          onEndReached={() => {
            typeof onEndReached === "function" && onEndReached();
          }}
          contentContainerStyle={styles.listWrapper}
        />
      ) : null}
      {product && (
        <AddEditCartItemModal
          modalState={modalState}
          product={product}
          hideModal={hideModal}
        />
      )}
    </>
  );
};

export default ProductStack;

ProductStack.defaultProps = {
  showHeading: false,
};

const styles = StyleSheet.create({
  listMenu: {
    paddingHorizontal: 16,
    flexDirection: "row",
  },
  listMenuText: {
    alignSelf: "center",
    flexGrow: 1,
    width: 122,
    color: Colors.shades._7,
    ...FontStyles.size13,
    ...FontStyles.lh13,
    ...FontStyles.normal,
  },
  listMenuIcon: {
    alignSelf: "center",
    padding: 3,
    marginLeft: 10,
  },
  listWrapper: {
    paddingBottom: 100,
  },
  itemWrapper: {
    width: "50%",
  },
  itemWrapperList: {},
  itemWrapperBorder: {
    borderLeftColor: Colors.borderColor,
    borderLeftWidth: 1,
    borderStyle: "solid",
  },
  dummyCard: {
    borderColor: "#E5EAF0",
    borderWidth: 1,
    borderStyle: "solid",
    borderRadius: 6,
    backgroundColor: Colors.whiteColor,
    flex: 1,
    opacity: 0.6,
    minHeight: 130,
  },
  sectionHeader: {
    ...FontStyles.size14,
    ...FontStyles.lh22,
    ...FontStyles.medium,
    paddingHorizontal: 18,
    paddingVertical: 12,
    // marginBottom: 8,
  },
  sectionHeader2: {
    borderTopColor: Colors.borderColor,
    borderTopWidth: 1,
    backgroundColor: Colors.whiteColor,
  },
  emptyContainer: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 30,
    marginBottom: 20,
  },
  emptyImage: {
    width: 50,
    height: 40,
  },
  emptyText: {
    maxWidth: 215,
    ...FontStyles.normal,
    ...FontStyles.size14,
    ...FontStyles.lh20,
    ...FontStyles.gray,
    textAlign: "center",
    marginTop: 18,
  },
});
